/**
 * One-click sign-in: pick a client, press it, you are in.
 *
 * There are two modes, and the difference between them is where the password
 * lives.
 *
 * ── Development (default) ────────────────────────────────────────────────
 * THE CREDENTIALS NEVER REACH THE BROWSER BUNDLE. They are read by the dev
 * server (Node) from UNPREFIXED variables, and served on a route that exists
 * only while `react-scripts start` is running. The panel fetches them at click
 * time.
 *
 * ── Bundled (opt-in, for deployed builds) ────────────────────────────────
 * REACT_APP_QUICK_LOGIN=on compiles the credentials INTO the JavaScript,
 * because a static site served by nginx has no server-side place to keep
 * them. Everyone who can load the login page can read those passwords out of
 * main.js and sign in from anywhere. That is not a side effect of this mode,
 * it IS this mode. Use throwaway accounts and keep the site behind a network
 * gate. See QUICK_LOGIN.md.
 *
 * ── Why the prefix rule is what it is ────────────────────────────────────
 * The first version used REACT_APP_QUICK_LOGIN_* for development too, and
 * that leaked. The mechanism is worth writing down, because it is easy to
 * repeat:
 *
 *   - Create React App loads .env.local for PRODUCTION builds too, not just
 *     development (config/env.js includes it whenever NODE_ENV is not "test").
 *   - Every REACT_APP_* variable is collapsed into ONE DefinePlugin key,
 *     `'process.env': { …all of them… }`.
 *   - Where the source reads a REACT_APP_ variable that is not set at build
 *     time, DefinePlugin cannot fold the member access, so it substitutes the
 *     WHOLE object — every other REACT_APP_ value with it.
 *
 * So `npm run build` on a machine with a populated .env.local inlined every
 * quick-login e-mail and password into main.js, three times over. The panel
 * itself was correctly dead-code-eliminated, which is exactly why grepping for
 * the panel's own strings could not detect it — the leak carries no panel
 * string, only the env keys and their values.
 *
 * Two rules follow, and both are load-bearing:
 *
 *   1. Development credentials carry NO prefix, so the bundler never sees them.
 *   2. Every read below is a STATIC member access, one per variable. A computed
 *      `process.env[key]` cannot be folded and would inline the whole object.
 *      That is also why .env declares all seven names with empty defaults: an
 *      unset variable is an unfoldable read.
 */

/** Selectable clients. Mirrors QUICK_LOGIN_CLIENTS in src/setupProxy.js, which
 *  is CommonJS and cannot import this module. */
const CLIENTS = [
  { key: "ergo", label: "Ergo", environment: "Dev" },
  { key: "bajaj", label: "Bajaj", environment: "Preprod" },
  { key: "hegic", label: "Hegic", environment: "Preprod" },
];

const pair = (email, password) =>
  email && password ? { email, password } : null;

/**
 * Credentials compiled into this bundle, or null.
 *
 * A switch rather than a lookup table so each read is its own static member
 * access, and lazy rather than module-level so tests can set the variables.
 */
const bundledCredentials = (key) => {
  switch (key) {
    case "ergo":
      return pair(
        process.env.REACT_APP_QUICK_LOGIN_ERGO_EMAIL,
        process.env.REACT_APP_QUICK_LOGIN_ERGO_PASSWORD,
      );
    case "bajaj":
      return pair(
        process.env.REACT_APP_QUICK_LOGIN_BAJAJ_EMAIL,
        process.env.REACT_APP_QUICK_LOGIN_BAJAJ_PASSWORD,
      );
    case "hegic":
      return pair(
        process.env.REACT_APP_QUICK_LOGIN_HEGIC_EMAIL,
        process.env.REACT_APP_QUICK_LOGIN_HEGIC_PASSWORD,
      );
    default:
      return null;
  }
};

const bundledEnvNames = (key) => {
  const base = `REACT_APP_QUICK_LOGIN_${String(key).toUpperCase()}`;
  return [`${base}_EMAIL`, `${base}_PASSWORD`];
};

const devEnvNames = (key) => {
  const base = `QUICK_LOGIN_${String(key).toUpperCase().replace(/[^A-Z0-9]+/g, "_")}`;
  return [`${base}_EMAIL`, `${base}_PASSWORD`];
};

/**
 * Credentials come from the bundle rather than the dev server.
 *
 * Opt-IN, and only outside development: a deploy that does not ask for this
 * gets no panel, and a developer keeps the dev-server route even if the
 * prefixed variables happen to be set.
 */
export const isBundledQuickLogin = () =>
  process.env.NODE_ENV !== "development" &&
  process.env.REACT_APP_QUICK_LOGIN === "on";

const isDevQuickLogin = () =>
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_QUICK_LOGIN !== "off";

export const isQuickLoginEnabled = () =>
  isDevQuickLogin() || isBundledQuickLogin();

/** Served by src/setupProxy.js, which only the dev server ever loads. */
export const QUICK_LOGIN_ROUTE = "/__dev/quick-login";

const jsonOrNull = async (response) => {
  if (!response?.ok) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
};

/**
 * What the panel should offer: every selectable client, each marked ready or
 * not, so a missing credential is visible before it is clicked rather than
 * arriving as a failed login. Passwords are NOT in this response.
 */
export const fetchQuickLoginOptions = async (fetcher = fetch) => {
  if (isBundledQuickLogin()) {
    return CLIENTS.map((client) => {
      const credentials = bundledCredentials(client.key);
      return {
        ...client,
        ready: Boolean(credentials),
        email: credentials?.email || "",
        envVars: bundledEnvNames(client.key),
      };
    });
  }
  if (!isDevQuickLogin()) return [];
  try {
    const data = await jsonOrNull(await fetcher(QUICK_LOGIN_ROUTE));
    return Array.isArray(data?.clients) ? data.clients : [];
  } catch {
    // The dev server may not have the route yet (it is read once at startup).
    return [];
  }
};

/**
 * The credentials for one client. In development they are fetched at the
 * moment of the click, so they live no longer than the sign-in itself; in a
 * bundled build they were compiled in and have no lifetime to speak of.
 */
export const fetchCredentials = async (clientKey, fetcher = fetch) => {
  if (!clientKey) return null;
  if (isBundledQuickLogin()) return bundledCredentials(clientKey);
  if (!isDevQuickLogin()) return null;
  try {
    const data = await jsonOrNull(
      await fetcher(`${QUICK_LOGIN_ROUTE}/${encodeURIComponent(clientKey)}`),
    );
    return data?.email && data?.password
      ? { email: data.email, password: data.password }
      : null;
  } catch {
    return null;
  }
};

/** Where the panel tells someone to put a missing credential. */
export const quickLoginEnvNames = (key) =>
  isBundledQuickLogin() ? bundledEnvNames(key) : devEnvNames(key);
